import React, { useEffect, useState } from 'react';
import { AlertTriangle, Package, CheckCircle, XCircle, ArrowDown, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function StockAlertsPublicPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showOk, setShowOk] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);

  const loadAlerts = async () => {
    setLoading(true);
    try {
      const res = await fetch(API_URL + '/api/public/stock-alerts/hit-grill');
      if (!res.ok) throw new Error('Erro ao carregar');
      const json = await res.json();
      setData(json);
      setLastUpdate(new Date());
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAlerts(); }, []);

  const zerados = data?.alerts?.filter(a => a.status === 'ZERADO') || [];
  const baixos = data?.alerts?.filter(a => a.status === 'BAIXO') || [];
  const okItems = data?.ok_items || [];

  const formatQty = (qty, unit) => {
    if (unit === 'G' && qty >= 1000) return (qty / 1000).toFixed(1) + ' kg';
    if (unit === 'ML' && qty >= 1000) return (qty / 1000).toFixed(1) + ' L';
    return qty.toFixed(0) + ' ' + unit.toLowerCase();
  };

  if (loading && !data) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a' }}>
        <div style={{ textAlign: 'center', color: '#94a3b8' }}>
          <RefreshCw size={40} style={{ animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
          <p>Carregando estoque...</p>
          <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a' }}>
        <div style={{ textAlign: 'center', color: '#ef4444' }}>
          <XCircle size={40} style={{ margin: '0 auto 16px' }} />
          <p>{error}</p>
          <button onClick={loadAlerts} style={{ marginTop: 16, padding: '8px 24px', background: '#1e293b', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 8, cursor: 'pointer' }}>Tentar novamente</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#0f172a', color: '#e2e8f0', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .alert-row:hover { background: #1e293b !important; }
        .pulse { animation: pulse-anim 2s ease-in-out infinite; }
        @keyframes pulse-anim { 0%,100% { opacity: 1; } 50% { opacity: 0.5; } }
      `}</style>
      
      <div style={{ background: '#1e293b', borderBottom: '1px solid #334155', padding: '20px 24px' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: '#f8fafc', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Package size={22} />
              {data?.store_name || 'Hit Grill'} - Estoque
            </h1>
            <p style={{ fontSize: 13, color: '#94a3b8', margin: '4px 0 0' }}>
              {data?.total_items} itens | {data?.total_alerts} alertas
              {lastUpdate && (' | ' + lastUpdate.toLocaleTimeString('pt-BR'))}
            </p>
          </div>
          <button 
            onClick={loadAlerts}
            disabled={loading}
            style={{ padding: '8px 16px', background: '#334155', color: '#e2e8f0', border: 'none', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
          >
            <RefreshCw size={14} style={loading ? { animation: 'spin 1s linear infinite' } : {}} />
            Atualizar
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 16px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
          <div style={{ background: '#7f1d1d', borderRadius: 12, padding: 16, textAlign: 'center' }}>
            <XCircle size={24} style={{ color: '#fca5a5', marginBottom: 4 }} />
            <div style={{ fontSize: 28, fontWeight: 700, color: '#fca5a5' }}>{zerados.length}</div>
            <div style={{ fontSize: 12, color: '#fca5a5', opacity: 0.8 }}>Zerados</div>
          </div>
          <div style={{ background: '#78350f', borderRadius: 12, padding: 16, textAlign: 'center' }}>
            <AlertTriangle size={24} style={{ color: '#fcd34d', marginBottom: 4 }} />
            <div style={{ fontSize: 28, fontWeight: 700, color: '#fcd34d' }}>{baixos.length}</div>
            <div style={{ fontSize: 12, color: '#fcd34d', opacity: 0.8 }}>Baixo</div>
          </div>
          <div style={{ background: '#14532d', borderRadius: 12, padding: 16, textAlign: 'center' }}>
            <CheckCircle size={24} style={{ color: '#86efac', marginBottom: 4 }} />
            <div style={{ fontSize: 28, fontWeight: 700, color: '#86efac' }}>{okItems.length}</div>
            <div style={{ fontSize: 12, color: '#86efac', opacity: 0.8 }}>OK</div>
          </div>
        </div>

        {zerados.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <h2 style={{ fontSize: 16, fontWeight: 600, color: '#fca5a5', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
              <XCircle size={18} /> Estoque Zerado ({zerados.length})
            </h2>
            <div style={{ background: '#1e293b', borderRadius: 12, border: '1px solid #7f1d1d', overflow: 'hidden' }}>
              {zerados.map((item, i) => (
                <div key={i} className="alert-row" style={{ padding: '12px 16px', borderBottom: i < zerados.length - 1 ? '1px solid #334155' : 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <span style={{ fontWeight: 500, fontSize: 14 }}>{item.name}</span>
                    {item.min_stock > 0 && (
                      <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 8 }}>min: {formatQty(item.min_stock, item.unit)}</span>
                    )}
                  </div>
                  <span className="pulse" style={{ fontSize: 13, fontWeight: 600, color: '#ef4444', background: '#7f1d1d', padding: '3px 10px', borderRadius: 6 }}>0</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {baixos.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <h2 style={{ fontSize: 16, fontWeight: 600, color: '#fcd34d', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
              <ArrowDown size={18} /> Estoque Baixo ({baixos.length})
            </h2>
            <div style={{ background: '#1e293b', borderRadius: 12, border: '1px solid #78350f', overflow: 'hidden' }}>
              {baixos.map((item, i) => (
                <div key={i} className="alert-row" style={{ padding: '12px 16px', borderBottom: i < baixos.length - 1 ? '1px solid #334155' : 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <span style={{ fontWeight: 500, fontSize: 14 }}>{item.name}</span>
                    <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 8 }}>min: {formatQty(item.min_stock, item.unit)}</span>
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#f59e0b', background: '#78350f', padding: '3px 10px', borderRadius: 6 }}>
                    {formatQty(item.saldo, item.unit)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <button 
            onClick={() => setShowOk(!showOk)}
            style={{ width: '100%', padding: '12px 16px', background: '#1e293b', border: '1px solid #334155', borderRadius: 12, color: '#86efac', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 14, fontWeight: 500 }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <CheckCircle size={18} /> Itens OK ({okItems.length})
            </span>
            <span style={{ fontSize: 12, color: '#94a3b8' }}>{showOk ? 'Esconder' : 'Mostrar'}</span>
          </button>
          {showOk && (
            <div style={{ background: '#1e293b', borderRadius: '0 0 12px 12px', border: '1px solid #334155', borderTop: 'none', overflow: 'hidden', marginTop: -1 }}>
              {okItems.map((item, i) => (
                <div key={i} className="alert-row" style={{ padding: '10px 16px', borderBottom: i < okItems.length - 1 ? '1px solid #0f172a' : 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 13 }}>{item.name}</span>
                  <span style={{ fontSize: 13, color: '#86efac' }}>{formatQty(item.saldo, item.unit)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
